import { useSelector } from 'react-redux';
import type { FunctionComponent } from 'react';

import { Category } from '../../../../../shared/issuesV2';
import { useDispatchOnMount } from '../../../../hooks';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import ViewPathModal from '../../modals/ViewPathModal';
import ActionButtons from '../ActionButtons';
import BackToIssues from '../BackToIssues';
import Comments from '../Comments';
import Errors from '../Errors';
import { IssueDetailsLicensesTable } from '../IssueContent/LicensesTable';
import IssueDetailsModalsProvider from '../IssueDetailsModalsContext';
import LicensingFooter from '../Licensing/Footer';
import ProjectsTable from '../ProjectsTable';
import { QualityIssueDisputeFooter as QualityFooter } from '../Quality/Footer';
import Status from '../Status';
import Tabs from '../Tabs';
import useIssueDetailsTabs from '../Tabs/useIssueDetailsTabs';
import VulnerabilityFooter from '../Vulnerability/Footer';
import { MalwareIssueDisputeFooter as MalwareFooter } from '../Vulnerability/MalwareFooter';
import type { IssuesContainerProps } from '../../IssuesContainer';

import FullPageLoading from './FullPageLoading';
import Header from './Header';
import IssueTab from './IssueTab';

import styles from '../IssueDetails.module.less';

/**
 * Container/Route component for `../issues/:category/:id`, which displays
 * the UI matching the drawer, but displayed in
 */
const IssueDetails: FunctionComponent<IssuesContainerProps> = ({ params }) => {
  const { isLoading, errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusIssueDetails);
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const [activeTab, setActiveTab] = useIssueDetailsTabs(false);

  // @ts-expect-error - This is a valid action and call signature, but TS is not able to infer the correct type
  // from Redux
  useDispatchOnMount(() => IssuesV2Store.actions.fetchIssueDetailsForFullPage(params.id as unknown as number));

  return (
    <IssueDetailsModalsProvider>
      <BackToIssues />
      {isLoading ? (
        <FullPageLoading />
      ) : (
        <div className={styles.body}>
          {errors?.length ? (
            <Errors errors={errors} />
          ) : (
            <>
              <Header category={category} />
              <Status />
              <ActionButtons />
              <Tabs activeTab={activeTab} onChange={setActiveTab} />
              {activeTab === 'issue' && (
                <IssueTab category={category} />
              )}
              {activeTab === 'projects' && (
                <ProjectsTable />
              )}
              {activeTab === 'licenses' && (
                <IssueDetailsLicensesTable />
              )}
              {activeTab === 'comments' && (
                <div className={styles.commentsFullPage}>
                  <Comments />
                </div>
              )}
              {/* Both footers guard on the issue type, so exactly one of them renders. */}
              {category === Category.Vulnerability && (
                <>
                  <VulnerabilityFooter />
                  <MalwareFooter />
                </>
              )}
              {category === Category.Licensing && (
                <LicensingFooter />
              )}
              {category === Category.Quality && (
                <QualityFooter />
              )}
              {/* Note: This modal can be opened from multiple places in the drawer/details page, which is why it is rendered way up here in the component tree */}
              <ViewPathModal />
            </>
          )}
        </div>
      )}
    </IssueDetailsModalsProvider>
  );
};

export default IssueDetails;
